import { toast } from "sonner";
import i18n from "i18next";

// Vite emits `vite:preloadError` when a dynamic import fails to load — almost
// always because a fresh deploy replaced the chunk hash and the open tab is
// still on the old `index.html`. Show a Mori-voiced toast offering a reload.
//
// Pairs with the chunk-load detection in ErrorBoundary.tsx, which auto-reloads
// once if the user crashes before the toast can be acted on.

const RELOAD_GUARD_KEY = "mori:postPreloadErrorReload";

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed")
  );
}

export function initAppUpdateNotifier() {
  let shown = false;

  const showUpdateToast = () => {
    if (shown) return;
    shown = true;
    toast.message(i18n.t("error.update.title"), {
      description: i18n.t("error.update.description"),
      duration: Infinity,
      action: {
        label: i18n.t("error.update.reload"),
        onClick: () => {
          sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
          window.location.reload();
        },
      },
    });
  };

  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    showUpdateToast();
  });
}

// Used by ErrorBoundary to auto-reload once on a chunk-load crash. The
// sessionStorage guard prevents an infinite reload loop if the new deploy
// is also broken — second crash falls through to the normal error screen.
export function tryAutoReloadOnChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === "1") return false;
  sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  window.location.reload();
  return true;
}
